import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Input,
  Spinner,
  Alert,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import api from '../helpers/api';

const ServiceRequestModal = ({
  isOpen,
  toggle,
  requestData,
  onSubmit,
  isSubmitting,
  errors = {},
  apiError = "",
  locations = [],
  enumData = [],
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [docUploading, setDocUploading] = useState(false);

  // Asset search state
  const [assetSearchTerm, setAssetSearchTerm] = useState("");
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetDisplay, setSelectedAssetDisplay] = useState("");
  const [assets, setAssets] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;

  // Priority options from dynamic enums (same source as Work Orders)
  const priorityOptions = (enumData?.find(e => e.name === 'urgencyLevel')?.values || []).map(val => ({
    value: val,
    label: t(`shared.${val.toLowerCase()}`, val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
  }));

  // Custom select styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': { backgroundColor: '#2e669a' }
    })
  };

  // Fetch assets for search
  const fetchAssets = async () => {
    try {
      const params = {
        organizationId,
        count: 5,
        search: assetSearchTerm,
        hierarchy: false
      };
      const { data } = await api.get(`asset/all`, { params });
      setAssets(data?.response?.[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  // Handle asset selection
  const handleAssetSelect = (asset) => {
    const locationId = asset.currentLocation
      ? locations?.find(loc => loc.locationName === asset.currentLocation)?.id
      : null;

    setFormData(prev => ({
      ...prev,
      assetId: asset.id,
      locationId: locationId || prev.locationId || null,
    }));
    setSelectedAssetDisplay(`${asset.assetName}${asset.assetNumber ? ` - ${t('shared.id', 'ID')}: ${asset.assetNumber}` : ''} - ${asset.serialNumber} (${asset.currentLocation || t('shared.no_location', 'No location')})`);
    setAssetSearchTerm("");
    setShowAssetDropdown(false);
  };

  // Handle asset search change
  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearchTerm(value);
    setShowAssetDropdown(value.length > 0);
    if (value.length === 0) {
      setFormData(prev => ({
        ...prev,
        assetId: null,
      }));
      setSelectedAssetDisplay("");
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        id: requestData?.id || null,
        title: requestData?.title || '',
        description: requestData?.description || '',
        assetId: requestData?.assetId || requestData?.asset?.id || null,
        locationId: requestData?.locationId || null,
        urgencyLevel: requestData?.urgencyLevel || null,
        imageUrls: requestData?.imageUrls || [],
        documentUrls: requestData?.documentUrls || [],
      });

      // Set asset display if editing
      if (requestData?.asset) {
        setSelectedAssetDisplay(`${requestData.asset.assetName}${requestData.asset.assetNumber ? ` - ${t('shared.id', 'ID')}: ${requestData.asset.assetNumber}` : ''} - ${requestData.asset.serialNumber || ''} (${requestData.asset.currentLocation || t('shared.no_location', 'No location')})`);
      } else {
        setSelectedAssetDisplay("");
      }
      setAssetSearchTerm("");
      setShowAssetDropdown(false);
    }
  }, [isOpen, requestData]);

  // Fetch assets when search term changes
  useEffect(() => {
    if (organizationId && assetSearchTerm.length > 0) {
      fetchAssets();
    } else if (assetSearchTerm.length === 0) {
      setAssets([]);
    }
  }, [organizationId, assetSearchTerm]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setImageUploading(true);
    try {
      const formDataUpload = new FormData();
      files.forEach(file => formDataUpload.append('files', file));
      const { data } = await api.post('files/img-upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newUrls = (data.response || []).map(item => item.publicUrl);
      setFormData(prev => ({
        ...prev,
        imageUrls: [...(prev.imageUrls || []), ...newUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setImageUploading(false);
    }
  };

  // Handle document upload
  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setDocUploading(true);
    try {
      const formDataUpload = new FormData();
      files.forEach(file => formDataUpload.append('files', file));
      const { data } = await api.post('files/pdf-upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newUrls = (data.response || []).map(item => item.publicUrl);
      setFormData(prev => ({
        ...prev,
        documentUrls: [...(prev.documentUrls || []), ...newUrls]
      }));
    } catch (error) {
      console.error('Error uploading documents:', error);
    } finally {
      setDocUploading(false);
    }
  };

  // Remove image
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  // Remove document
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documentUrls: prev.documentUrls.filter((_, i) => i !== index)
    }));
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Location options
  const locationOptions = locations.map(loc => ({
    value: loc.id,
    label: loc.locationName
  }));

  const isEditMode = !!formData.id;

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <form onSubmit={handleSubmit}>
          {/* Header - Simple white style like Work Orders */}
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">
              {isEditMode ? t('business.components.service_request_modal.edit_title', 'Edit Service Request') : t('business.components.service_request_modal.add_title', 'Add Service Request')}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={toggle}
              aria-label={t('shared.close', 'Close')}
            />
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {apiError && (
              <Alert color="danger" className="mb-4">
                {apiError}
              </Alert>
            )}

            {/* Title Field */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="title" className="form-label fw-medium">
                    {t('shared.title', 'Title')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    value={formData.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder={t('shared.issue_title_placeholder', 'Brief description of the issue')}
                  />
                  {errors.title && <small className="text-danger">{errors.title}</small>}
                </div>
              </Col>
            </Row>

            {/* Asset Selection with Search */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="assetSearch" className="form-label fw-medium">
                    {t('shared.select_asset', 'Select Asset')}
                  </label>
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1 position-relative">
                      {selectedAssetDisplay ? (
                        <div
                          className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedAssetDisplay("");
                            setFormData(prev => ({
                              ...prev,
                              assetId: null,
                            }));
                          }}
                        >
                          <span>{selectedAssetDisplay}</span>
                          <FeatherIcon icon="x" size={16} className="text-muted" />
                        </div>
                      ) : (
                        <>
                          <Input
                            type="text"
                            id="assetSearch"
                            placeholder={t('shared.type_to_search_assets', 'Type to search assets...')}
                            value={assetSearchTerm}
                            onChange={handleAssetSearchChange}
                            onFocus={() => {
                              if (assetSearchTerm.length > 0) {
                                setShowAssetDropdown(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setShowAssetDropdown(false), 200);
                            }}
                          />
                          {showAssetDropdown && assets.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                              style={{
                                zIndex: 1050,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                top: '100%'
                              }}
                            >
                              {assets.map((asset) => (
                                <div
                                  key={asset.id}
                                  className="p-2 border-bottom cursor-pointer d-flex align-items-center"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleAssetSelect(asset)}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  <div className="flex-grow-1">
                                    <div className="fw-medium text-dark">{asset.assetName}{asset.assetNumber && <span className="text-muted"> ({t('shared.id', 'ID')}: {asset.assetNumber})</span>}</div>
                                    <small className="text-muted">
                                      {asset.serialNumber} • {asset.currentLocation}
                                    </small>
                                  </div>
                                  <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                                </div>
                              ))}
                            </div>
                          )}
                          {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                            <div
                              className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                              style={{ zIndex: 1050, top: '100%' }}
                            >
                              {t('shared.no_assets_found_matching', 'No assets found matching "{{searchTerm}}"', { searchTerm: assetSearchTerm })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="align-self-end">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        title={t('shared.filter_assets', 'Filter Assets')}
                        style={{ height: '38px' }}
                      >
                        <FeatherIcon icon="filter" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Location Dropdown - Full Width */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="locationId" className="form-label fw-medium">
                    {t('shared.location', 'Location')}
                  </label>
                  <Select
                    id="locationId"
                    options={locationOptions}
                    value={locationOptions.find(opt => opt.value === formData.locationId) || null}
                    onChange={(selected) => handleChange('locationId', selected?.value || null)}
                    isClearable
                    isSearchable
                    placeholder={t('shared.search_and_select_location', 'Search and select location...')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
            </Row>

            {/* Priority - Full Width */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="urgencyLevel" className="form-label fw-medium">
                    {t('shared.priority', 'Priority')}
                  </label>
                  <Select
                    id="urgencyLevel"
                    options={priorityOptions}
                    value={formData.urgencyLevel ? {
                      value: formData.urgencyLevel,
                      label: t(`shared.${formData.urgencyLevel.toLowerCase()}`, formData.urgencyLevel.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                    } : null}
                    onChange={(selected) => handleChange('urgencyLevel', selected?.value || null)}
                    isClearable
                    isSearchable
                    placeholder={t('shared.select_priority', 'Select Priority')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
            </Row>

            {/* Image Upload - Dashed Border Style */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('shared.images', 'Images')}</label>
                  <div
                    className="border rounded p-4 text-center"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: '#dee2e6',
                      backgroundColor: '#fafbfc'
                    }}
                  >
                    <input
                      type="file"
                      id="imageUploadInput"
                      className="d-none"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                    />
                    <label
                      htmlFor="imageUploadInput"
                      className={`btn btn-outline-primary mb-2 ${imageUploading ? 'disabled' : ''}`}
                      style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
                    >
                      {imageUploading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t('shared.uploading', 'Uploading...')}
                        </>
                      ) : (
                        <>
                          <FeatherIcon icon="upload" size={16} className="me-2" />
                          {t('shared.upload_images', 'Upload Images')}
                        </>
                      )}
                    </label>
                    <p className="text-muted small mb-0">
                      {imageUploading ? t('shared.wait_uploading_images', 'Please wait while images are being uploaded...') : t('shared.click_to_select_images', 'Click to select images to upload')}
                    </p>
                  </div>
                  {formData.imageUrls?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {formData.imageUrls.map((url, index) => (
                        <div
                          key={index}
                          className="position-relative rounded overflow-hidden"
                          style={{ width: '100px', height: '100px' }}
                        >
                          <img
                            src={url}
                            alt={`${t('shared.upload', 'Upload')} ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute d-flex align-items-center justify-content-center"
                            style={{
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              padding: 0,
                              borderRadius: '50%'
                            }}
                            onClick={() => removeImage(index)}
                          >
                            <FeatherIcon icon="x" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="description" className="form-label fw-medium">
                    {t('shared.description', 'Description')}
                  </label>
                  <textarea
                    id="description"
                    className="form-control"
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    placeholder={t('shared.description_placeholder', 'Provide detailed information about the issue...')}
                  />
                </div>
              </Col>
            </Row>

            {/* Files Upload - Dashed Border Style */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('shared.files', 'Files')}</label>
                  <div
                    className="border rounded p-4 text-center"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: '#dee2e6',
                      backgroundColor: '#fafbfc'
                    }}
                  >
                    <input
                      type="file"
                      id="fileUploadInput"
                      className="d-none"
                      multiple
                      onChange={handleDocUpload}
                      disabled={docUploading}
                    />
                    <label
                      htmlFor="fileUploadInput"
                      className={`btn btn-outline-primary mb-2 ${docUploading ? 'disabled' : ''}`}
                      style={{ cursor: docUploading ? 'not-allowed' : 'pointer' }}
                    >
                      {docUploading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t('shared.uploading', 'Uploading...')}
                        </>
                      ) : (
                        <>
                          <FeatherIcon icon="paperclip" size={16} className="me-2" />
                          {t('shared.attach_files', 'Attach Files')}
                        </>
                      )}
                    </label>
                    <p className="text-muted small mb-0">
                      {docUploading ? t('shared.wait_uploading_files', 'Please wait while files are being uploaded...') : t('shared.click_to_select_files', 'Click to select files to attach')}
                    </p>
                  </div>
                  {formData.documentUrls?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {formData.documentUrls.map((url, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center gap-2 bg-light rounded p-2 pe-3"
                        >
                          <FeatherIcon icon="file" size={16} className="text-muted" />
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-decoration-none small">
                            {t('shared.document', 'Document')} {index + 1}
                          </a>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-danger ms-2"
                            onClick={() => removeDocument(index)}
                          >
                            <FeatherIcon icon="x" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {/* Footer */}
          <div className="modal-footer bg-light border-top-0">
            <button type="button" className="btn btn-outline-secondary" onClick={toggle}>
              {t('shared.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || imageUploading || docUploading}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {isEditMode ? t('shared.updating', 'Updating...') : t('shared.submitting', 'Submitting...')}
                </>
              ) : (
                isEditMode ? t('business.components.service_request_modal.update_button', 'Update Request') : t('business.components.service_request_modal.create_button', 'Create Request')
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </Modal>
  );
};

export default ServiceRequestModal;
